import React, { useState, useEffect } from 'react';
import { usePlaybook } from '../../context/PlaybookContext';
import { useCRM } from '../../context/CRMContext';
import SafeIcon from '../../common/SafeIcon';

const PlaybookOverview = ({ projectId }) => {
  const { getPlaybook, updatePlaybook } = usePlaybook();
  const { currentUser } = useCRM();
  
  const playbook = getPlaybook(projectId);
  const [formData, setFormData] = useState(playbook);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setFormData(playbook);
  }, [playbook]);

  const handleSave = () => {
    updatePlaybook(projectId, formData);
    setIsEditing(false);
  };

  const isCustomer = currentUser.role === 'Customer_Viewer';

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header Info */}
      <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-xl font-black text-slate-900">Prosjektdefinisjon</h3>
            <p className="text-sm text-slate-500">Grunnlaget for samarbeidet.</p>
          </div>
          {!isCustomer && (
            <button 
              onClick={() => isEditing ? handleSave() : setIsEditing(true)} 
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${isEditing ? 'bg-emerald-500 text-white shadow-lg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              {isEditing ? 'Lagre endringer' : 'Rediger'}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Definisjon av suksess</label>
              {isEditing ? (
                <textarea 
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-primary-500"
                  rows={3}
                  value={formData.success_definition || ''}
                  onChange={e => setFormData({...formData, success_definition: e.target.value})}
                  placeholder="Hva må til for at dette er en suksess?"
                />
              ) : (
                <p className="text-sm text-slate-700 font-medium leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                  {formData.success_definition || 'Ingen definisjon satt.'}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">KPI-er</label>
              {isEditing ? (
                <input 
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-primary-500"
                  value={formData.kpis || ''}
                  onChange={e => setFormData({...formData, kpis: e.target.value})}
                  placeholder="F.eks. Økt salg med 10%"
                />
              ) : (
                <p className="text-sm text-slate-700 font-medium bg-slate-50 p-4 rounded-xl border border-slate-100">
                  {formData.kpis || '-'}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Go / No-Go Kriterier</label>
              {isEditing ? (
                <textarea 
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-primary-500"
                  rows={3}
                  value={formData.go_no_go || ''}
                  onChange={e => setFormData({...formData, go_no_go: e.target.value})}
                />
              ) : (
                <p className="text-sm text-slate-700 font-medium leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                  {formData.go_no_go || 'Ikke definert.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Internal Notes (Hidden for Customer) */}
      {!isCustomer && (
        <div className="bg-amber-50 p-8 rounded-[2rem] border border-amber-100 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <SafeIcon icon="🔒" className="text-6xl text-amber-900" />
          </div>
          <h3 className="text-sm font-black text-amber-900 uppercase tracking-widest mb-4 flex items-center gap-2">
            <SafeIcon icon="📝" /> Interne Notater (Kun Team)
          </h3>
          {isEditing ? (
            <textarea 
              className="w-full p-4 bg-white/50 border border-amber-200 rounded-xl text-sm text-amber-900 placeholder-amber-700/50 outline-none focus:ring-2 focus:ring-amber-400"
              rows={5}
              value={formData.internal_notes || ''}
              onChange={e => setFormData({...formData, internal_notes: e.target.value})}
              placeholder="Skriv notater som kunden IKKE skal se..."
            />
          ) : (
            <div className="prose prose-sm text-amber-800 whitespace-pre-wrap">
              {formData.internal_notes || 'Ingen interne notater.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlaybookOverview;